import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { shipmentsApi } from '../../api/endpoints';
import StatusBadge from '../../components/common/StatusBadge';
import { ShipmentDetail } from '../../types';
import Select from '../../components/common/Select';
import { FiSend, FiDownload, FiUpload, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';

const ShipmentDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    'items' | 'cartons' | 'labels' | 'packing'
  >('items');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState('PackingSlip');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchShipment = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await shipmentsApi.get(id);
      setShipment(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('shipments.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchShipment();
  }, [fetchShipment]);

  const handleSubmitToAmazon = useCallback(async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await shipmentsApi.submit(id);
      toast.success(t('shipments.sentToAmazon'));
      fetchShipment();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('shipments.sendToAmazonError'));
    } finally {
      setSubmitting(false);
    }
  }, [id, fetchShipment, t]);

  const handleDownloadLabel = useCallback(async () => {
    if (!id) return;
    try {
      const response = await shipmentsApi.getLabel(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `label-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('shipments.labelDownloadError'));
    }
  }, [id, t]);

  const handleRequestLabel = useCallback(async () => {
    if (!id) return;
    try {
      await shipmentsApi.requestLabel(id);
      toast.success(t('shipments.labelGenerated'));
      fetchShipment();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('shipments.labelGenerateError'));
    }
  }, [id, fetchShipment, t]);

  const handleUploadPackingDoc = useCallback(async () => {
    if (!id || !fileInputRef.current?.files?.length) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', fileInputRef.current.files[0]);
      formData.append('documentType', docType);
      await shipmentsApi.uploadPackingDoc(id, formData);
      toast.success(t('shipments.packingDocUploaded'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchShipment();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('shipments.packingDocUploadError'));
    } finally {
      setUploadingDoc(false);
    }
  }, [id, docType, fetchShipment, t]);

  const [fetchingSlip, setFetchingSlip] = useState(false);

  const handleFetchPackingSlip = useCallback(async () => {
    if (!id) return;
    setFetchingSlip(true);
    try {
      const response = await shipmentsApi.fetchPackingSlip(id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `packing_slip_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('shipments.packingSlipDownloaded'));
      fetchShipment();
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.warning(t('shipments.packingSlipUnavailable'));
      } else {
        toast.error(error.response?.data?.message || t('shipments.packingSlipDownloadError'));
      }
    } finally {
      setFetchingSlip(false);
    }
  }, [id, fetchShipment, t]);

  const handleDownloadPackingDoc = useCallback(async (docId: string, fileName: string) => {
    try {
      const response = await shipmentsApi.getPackingDoc(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('shipments.docDownloadError'));
    }
  }, [t]);

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="page-container page-container--wide">
        <div className="alert alert-danger">{t('shipments.notFound')}</div>
        <Link to="/shipments" className="btn btn-secondary">
          {t('shipments.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/shipments" className="btn btn-link">
            &larr; {t('shipments.title')}
          </Link>
          <h1>
            {t('shipments.shipmentTitle', { id: shipment.amazonShipmentId || shipment.id.substring(0, 8) })}
          </h1>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleSubmitToAmazon}
            disabled={submitting}
          >
            <FiSend /> {submitting ? t('shipments.submitting') : t('shipments.sendToAmazon')}
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadLabel}>
            <FiDownload /> {t('shipments.downloadLabel')}
          </button>
          <button className="btn btn-secondary" onClick={handleRequestLabel}>
            <FiRefreshCw /> {t('shipments.generateLabel')}
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-item">
          <span className="detail-label">{t('orders.poNumber')}</span>
          <span className="detail-value">
            <Link to={`/purchase-orders/${shipment.purchaseOrderId}`}>
              {shipment.purchaseOrderNumber}
            </Link>
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.amazonShipmentId')}</span>
          <span className="detail-value">{shipment.amazonShipmentId || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('common.status')}</span>
          <span className="detail-value">
            <StatusBadge type="shipment" value={shipment.status} />
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.shipDate')}</span>
          <span className="detail-value">{formatDate(shipment.shipDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.estimatedDelivery')}</span>
          <span className="detail-value">{formatDate(shipment.estimatedDeliveryDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.actualDelivery')}</span>
          <span className="detail-value">{formatDate(shipment.actualDeliveryDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.carrier')}</span>
          <span className="detail-value">{shipment.carrierName || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">SCAC</span>
          <span className="detail-value">{shipment.carrierScac || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.trackingNumber')}</span>
          <span className="detail-value">{shipment.trackingNumber || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Bill of Lading</span>
          <span className="detail-value">{shipment.billOfLadingNumber || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.shipFromAddress')}</span>
          <span className="detail-value">{shipment.shipFromAddress || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.shipToAddress')}</span>
          <span className="detail-value">{shipment.shipToAddress || '-'}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.cartons')}</span>
          <span className="detail-value">{shipment.totalCartons}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.weight')}</span>
          <span className="detail-value">
            {shipment.totalWeight} {shipment.weightUnit}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('shipments.volume')}</span>
          <span className="detail-value">
            {shipment.totalVolume} {shipment.volumeUnit}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t('orders.createdAt')}</span>
          <span className="detail-value">{formatDate(shipment.createdAt)}</span>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          {t('orders.items')} ({shipment.items.length})
        </button>
        <button
          className={`tab ${activeTab === 'cartons' ? 'active' : ''}`}
          onClick={() => setActiveTab('cartons')}
        >
          {t('shipments.cartons')} ({shipment.cartons.length})
        </button>
        <button
          className={`tab ${activeTab === 'labels' ? 'active' : ''}`}
          onClick={() => setActiveTab('labels')}
        >
          {t('shipments.labels')} ({shipment.labels.length})
        </button>
        <button
          className={`tab ${activeTab === 'packing' ? 'active' : ''}`}
          onClick={() => setActiveTab('packing')}
        >
          {t('shipments.packingDocuments')} ({shipment.packingDocuments.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'items' && (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>ASIN</th>
                  <th>{t('shipments.amazonProductId')}</th>
                  <th>{t('shipments.shippedQuantity')}</th>
                  <th>{t('shipments.unit')}</th>
                </tr>
              </thead>
              <tbody>
                {shipment.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.itemSequenceNumber}</td>
                    <td>{item.asin || '-'}</td>
                    <td>{item.amazonProductId}</td>
                    <td>{item.shippedQuantity}</td>
                    <td>{item.quantityUnitOfMeasure}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'cartons' && (
          <div className="table-responsive">
            {shipment.cartons.length === 0 ? (
              <p className="text-muted">{t('shipments.noCartons')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('shipments.cartonId')}</th>
                    <th>{t('shipments.dimensions')}</th>
                    <th>{t('shipments.weight')}</th>
                    <th>{t('shipments.tracking')}</th>
                    <th>SSCC</th>
                    <th>{t('orders.items')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shipment.cartons.map((carton) => (
                    <tr key={carton.id}>
                      <td>{carton.cartonSequenceNumber}</td>
                      <td>{carton.cartonId}</td>
                      <td>
                        {carton.length} x {carton.width} x {carton.height}{' '}
                        {carton.dimensionUnit}
                      </td>
                      <td>
                        {carton.weight} {carton.weightUnit}
                      </td>
                      <td>{carton.trackingNumber || '-'}</td>
                      <td>{carton.sscc || '-'}</td>
                      <td>
                        {carton.cartonItems.map((ci) => (
                          <div key={ci.id}>
                            {ci.amazonProductId}: {ci.quantity} {t('shipments.pcs')}
                            {ci.expirationDate &&
                              ` (exp: ${formatDate(ci.expirationDate)})`}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'labels' && (
          <div className="table-responsive">
            {shipment.labels.length === 0 ? (
              <p className="text-muted">{t('shipments.noLabels')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('shipments.labelType')}</th>
                    <th>{t('shipments.fileName')}</th>
                    <th>{t('shipments.contentType')}</th>
                    <th>{t('shipments.amazonLabelId')}</th>
                    <th>{t('orders.createdAt')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shipment.labels.map((label) => (
                    <tr key={label.id}>
                      <td>{label.labelType}</td>
                      <td>{label.fileName}</td>
                      <td>{label.contentType}</td>
                      <td>{label.amazonLabelId || '-'}</td>
                      <td>{formatDate(label.createdAt)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleDownloadLabel}
                        >
                          <FiDownload /> {t('common.download')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'packing' && (
          <div>
            <div className="table-responsive">
              {shipment.packingDocuments.length === 0 ? (
                <p className="text-muted">{t('shipments.noPackingDocuments')}</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('shipments.docType')}</th>
                      <th>{t('shipments.fileName')}</th>
                      <th>{t('shipments.contentType')}</th>
                      <th>{t('orders.notes')}</th>
                      <th>{t('orders.createdAt')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipment.packingDocuments.map((doc) => (
                      <tr key={doc.id}>
                        <td>{doc.documentType}</td>
                        <td>{doc.fileName}</td>
                        <td>{doc.contentType}</td>
                        <td>{doc.notes || '-'}</td>
                        <td>{formatDate(doc.createdAt)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() =>
                              handleDownloadPackingDoc(doc.id, doc.fileName)
                            }
                          >
                            <FiDownload /> {t('common.download')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="card" style={{ marginTop: '1rem' }}>
              <div className="card-header">
                <h3>{t('shipments.uploadPackingDoc')}</h3>
              </div>
              <div className="card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('shipments.docType')}</label>
                    <Select
                      options={[
                        { value: 'PackingSlip', label: t('shipments.packingSlip') },
                        { value: 'BillOfLading', label: t('shipments.billOfLading') },
                        { value: 'CommercialInvoice', label: t('shipments.commercialInvoice') },
                        { value: 'Other', label: t('shipments.other') },
                      ]}
                      value={docType}
                      onChange={(value) => setDocType(value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('shipments.file')}</label>
                    <input
                      type="file"
                      className="form-control"
                      ref={fileInputRef}
                    />
                  </div>
                  <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleUploadPackingDoc}
                      disabled={uploadingDoc}
                    >
                      <FiUpload />{' '}
                      {uploadingDoc ? t('shipments.uploading') : t('shipments.upload')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleFetchPackingSlip}
                      disabled={fetchingSlip}
                      style={{ marginLeft: '0.5rem' }}
                    >
                      <FiDownload />{' '}
                      {fetchingSlip ? t('shipments.downloadingFromAmazon') : t('shipments.downloadPackingSlipFromAmazon')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipmentDetailPage;
